import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, AuthContextType } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper function to check if token is valid
const isValidToken = (token: string | null): boolean => {
  return !!token && token.length > 20 && !token.startsWith('undefined');
};

// Helper function to clear all authentication data
const clearAuthData = () => {
  localStorage.removeItem('token');
  // Clear any cookies that might exist
  document.cookie.split(";").forEach(function(c) { 
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
  });
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [serverAvailable, setServerAvailable] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async (retryCount = 0) => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          // Check if token is valid before making API call
          if (!isValidToken(token)) {
            clearAuthData();
            setUser(null);
            setLoading(false);
            return;
          }
          
          // First check if server is reachable
          const isServerHealthy = await authAPI.healthCheck();
          if (!isServerHealthy) {
            console.warn('Server health check failed - skipping auth check');
            setServerAvailable(false);
            setUser(null);
            setLoading(false);
            return;
          }
          
          setServerAvailable(true);
          
          const currentUser = await authAPI.getCurrentUser();
          setUser(currentUser);
        }
      } catch (error: any) {
        console.log('Auth check error:', error);
        
        // Handle different types of errors
        if (error.response?.status === 401) {
          // Unauthorized - clear auth data
          clearAuthData();
          setUser(null);
        } else if (error.message === 'Network Error' && retryCount < 2) {
          // Network error - retry up to 2 times with exponential backoff
          console.warn(`Network error during auth check (attempt ${retryCount + 1}/3) - retrying...`);
          setTimeout(() => {
            checkAuth(retryCount + 1);
          }, Math.pow(2, retryCount) * 1000); // 1s, 2s delays
          return; // Don't set loading to false yet
        } else if (error.message === 'Network Error') {
          // Final network error - give up
          console.warn('Network error during auth check - server may be unavailable');
          setServerAvailable(false);
          setUser(null);
        } else {
          // Other errors - log but don't clear token
          console.error('Unexpected error during auth check:', error);
          setUser(null);
        }
      } finally {
        if (retryCount === 0) { // Only set loading to false on first attempt
          setLoading(false);
        }
      }
    };

    checkAuth();
  }, []);

  // Periodically check server health when offline
  useEffect(() => {
    if (serverAvailable) return;

    const interval = setInterval(async () => {
      try {
        const isHealthy = await authAPI.healthCheck();
        if (isHealthy) {
          console.log('Server is back online!');
          setServerAvailable(true);
          // Optionally retry auth check
          const token = localStorage.getItem('token');
          if (token && isValidToken(token)) {
            try {
              const currentUser = await authAPI.getCurrentUser();
              setUser(currentUser);
            } catch (error) {
              console.log('Auth retry failed:', error);
            }
          }
        }
      } catch (error) {
        console.log('Health check failed:', error);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [serverAvailable]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      const { user: loggedInUser, token } = await authAPI.login(credentials);
      localStorage.setItem('token', token);
      setUser(loggedInUser);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Try to call logout API to invalidate token on server
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API failed:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Always clear local authentication data
      clearAuthData();
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    serverAvailable,
    setServerAvailable,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 